import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import FieldError from '../../../shared/fields-errors/field-error';
import { SAVE_FUNDRAISING_SETTINGS } from '../../../constants/endpoints';
import { SPOTLIGHT_PRESET_COLORS } from '../../../utils/utils';

const FundraisingSettings = React.forwardRef(({ onClose, onSaved, fundraisingSettings, campaignList = [] }, ref) => {
    const [loader, setLoader] = useState(false);

    const defaultValues = { type: '', campaign_id: '', show_pledge_qr: false, show_name_panel: true, show_confetti: true, display_layout:"classic", theme_color: '#155C4E', starting_donated_amount: 0, starting_pledged_amount: 0 };

    const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({ defaultValues });

    const watchedType = watch('type');
    const watchedLayout = watch('display_layout');
    const watchedColor = watch('theme_color');

    /* ── Methods ── */
    const handleClose = () => {
        onClose();
        reset(defaultValues);
    };

    const assignCampaign = (formValues) => {
        const { type: _type, ...payload } = formValues;
        setLoader(true);
        axios.post(SAVE_FUNDRAISING_SETTINGS, payload)
            .then((res) => {
                setLoader(false);
                if (res?.data?.status) {
                    onSaved?.();
                    handleClose();
                } else {
                    alert(res?.data?.message || 'Failed to save settings');
                }
            })
            .catch(() => {
                setLoader(false);
                alert('Failed to save settings');
            });
    };

    useEffect(() => {
        if (fundraisingSettings?.id) {
            reset({
                campaign_id: fundraisingSettings?.campaign?.id,
                type: fundraisingSettings?.campaign?.type,
                id: fundraisingSettings?.id,
                show_pledge_qr: fundraisingSettings?.show_pledge_qr,
                show_name_panel: fundraisingSettings?.show_name_panel,
                show_confetti: fundraisingSettings?.show_confetti,
                display_layout: fundraisingSettings?.display_layout,
                theme_color: fundraisingSettings?.theme_color || '#155C4E',
                starting_donated_amount: Number(fundraisingSettings?.starting_donated_amount || 0) / 100,
                starting_pledged_amount: Number(fundraisingSettings?.starting_pledged_amount || 0) / 100,
            });
        }
    }, [campaignList, fundraisingSettings]);

    const filteredCampaigns = campaignList.filter((item) => {
        if (!watchedType) return false;
        return watchedType === 'product' ? item?.type === 'product' : item?.type !== 'product';
    });

    return (
        <Form onSubmit={handleSubmit(assignCampaign)}>


            {/* ── Body ── */}

            {/* Type selector */}
            <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                    <span className="text-danger me-1">*</span>Select Type
                </Form.Label>
                <Form.Select
                    style={{ fontSize: 13 }}
                    {...register('type', { required: 'This field is required' })}
                >
                    <option value="">-- Select --</option>
                    <option value="product">Product</option>
                    <option value="campaign">Campaign</option>
                </Form.Select>
                {errors?.type && <FieldError message={errors.type.message} />}
            </Form.Group>

            {/* Campaign / Product selector */}
            <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#555' }} className="text-capitalize">
                    <span className="text-danger me-1">*</span>
                    Select {watchedType || 'Campaign'}
                </Form.Label>
                <Form.Select
                    style={{ fontSize: 13 }}
                    {...register('campaign_id', { required: 'This field is required' })}
                >
                    <option value="">-- Select --</option>
                    {filteredCampaigns.map((item, i) => (
                        <option key={i} value={item?.id}>{item?.campaign_title}</option>
                    ))}
                </Form.Select>
                {errors?.campaign_id && <FieldError message={errors.campaign_id.message} />}
            </Form.Group>

            {/* Layout Selection */}
            <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#555' }} className="text-capitalize">
                    <span className="text-danger me-1">*</span>
                    Display Layout
                </Form.Label>
                <Form.Select
                    style={{ fontSize: 13 }}
                    {...register('display_layout', { required: 'This field is required' })}
                >
                    <option value={'classic'}>Classic — Gradient with QR focus</option>
                    <option value={'spotlight'}>Spotlight — Clean with live feed</option>
                </Form.Select>
                {errors?.campaign_id && <FieldError message={errors.campaign_id.message} />}
            </Form.Group>

            {/* Starting Amounts — baseline the live screen counts up from */}
            <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                    Starting Amounts
                </Form.Label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                        { field: 'starting_donated_amount', label: 'Donated' },
                        { field: 'starting_pledged_amount', label: 'Pledged' },
                    ].map(({ field, label }) => (
                        <div key={field} style={{ flex: '0 1 150px' }}>
                            <span style={{ display: 'block', fontSize: 12, color: '#777', marginBottom: 4 }}>
                                {label}
                            </span>
                            <div style={{ position: 'relative' }}>
                                <span
                                    style={{
                                        position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                                        fontSize: 13, color: '#999', pointerEvents: 'none',
                                    }}
                                >
                                    $
                                </span>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0"
                                    style={{ fontSize: 13, paddingLeft: 22 }}
                                    {...register(field, { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' } })}
                                    isInvalid={!!errors?.[field]}
                                />
                            </div>
                            {errors?.[field] && <FieldError message={errors[field].message} />}
                        </div>
                    ))}
                </div>
                <span style={{ display: 'block', fontSize: 11.5, color: '#888', marginTop: 6 }}>
                    The screen starts at these numbers and adds everything raised from today onward.
                    Leave both at $0 to show only what comes in during this event.
                </span>
            </Form.Group>

            {/* Spotlight theme color — only relevant for the spotlight layout */}
            {watchedLayout === 'spotlight' && (
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                        Spotlight Theme Color
                    </Form.Label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        {SPOTLIGHT_PRESET_COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                title={c}
                                onClick={() => setValue('theme_color', c, { shouldDirty: true })}
                                style={{
                                    width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
                                    border: (watchedColor || '').toLowerCase() === c.toLowerCase() ? '2px solid #1a1a2e' : '2px solid #fff',
                                    boxShadow: '0 0 0 1px #e8eaed',
                                }}
                            />
                        ))}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                            <input
                                type="color"
                                {...register('theme_color')}
                                style={{ width: 30, height: 30, padding: 0, border: '1px solid #e8eaed', borderRadius: 6, cursor: 'pointer', background: 'transparent' }}
                            />
                            <input
                                type="text"
                                value={watchedColor || ''}
                                maxLength={7}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setValue('theme_color', v, { shouldDirty: true });
                                }}
                                style={{ width: 88, fontSize: 13, padding: '4px 8px', border: '1px solid #e8eaed', borderRadius: 6, textTransform: 'uppercase' }}
                            />
                        </span>
                    </div>
                </Form.Group>
            )}

            {/* Settings checkboxes (not for offline mode) */}

            <div
                style={{
                    marginTop: 6,
                    padding: '12px 14px',
                    background: 'var(--bg, #f4f5f7)',
                    borderRadius: 'var(--radius-md, 8px)',
                    border: '1px solid var(--border, #e8eaed)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                }}
            >
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #888)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: 0 }}>
                    Display Options
                </p>

                {[
                    { field: 'show_pledge_qr', label: 'Show Pledge QR Code' },
                    { field: 'show_name_panel', label: 'Show Donor Name Panel' },
                    { field: 'show_confetti', label: 'Show Confetti' },
                ].map(({ field, label }) => (
                    <label
                        key={field}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', margin: 0 }}
                    >
                        <input
                            type="checkbox"
                            style={{ width: 15, height: 15, accentColor: 'var(--brand, #0047AB)', cursor: 'pointer', flexShrink: 0 }}
                            {...register(field)}
                        />
                        <span style={{ fontSize: 13, color: '#444' }}>{label}</span>
                    </label>
                ))}
            </div>


            <div className='d-flex justify-content-end align-items-center mt-3'>
                <Button variant="light" className='me-2 mb-0' onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={loader}
                    className={`${loader ? 'position-relative button--loading' : ''} mb-0`}
                >
                    {'Save'}
                </Button>
            </div>
        </Form>
    );
});

export default FundraisingSettings;